<template>
  <div
    class="base-toggle"
    :disabled="disabled"
    :class="{ 'base-toggle--is-small': isSmall }"
    v-on:click="handleClick"
    v-tooltip="tooltipText"
  >
    <label class="base-toggle__switch" @click.prevent="handleToggle">
      <input
        class="base-toggle__input"
        type="checkbox"
        :key="toggleKey"
        :checked="value"
        :aria-label="ariaLabel || null"
        @click.prevent="handleToggle"
        @keydown.space.prevent="handleToggle"
      />
      <span class="base-toggle__slider"></span>
    </label>

    <div v-if="showBothLabels" class="base-toggle__label">
      <span class="base-toggle__label-on" v-if="value">{{ labelOn }}</span>
      <span class="base-toggle__label-off" v-else>{{ labelOff }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BaseToggle',
  props: {
    toggleKey: {
      type: String,
      default: 'base-toggle',
    },
    tooltipText: {
      type: String,
      default: '',
    },
    /** Accessible name for the checkbox, which sits in an otherwise empty <label>. */
    ariaLabel: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Boolean,
      required: true,
    },
    isSmall: {
      type: Boolean,
      default: false,
    },
    showBothLabels: {
      type: Boolean,
      default: false,
    },
    labelOff: {
      type: String,
      default: 'Non',
    },
    labelOn: {
      type: String,
      default: 'Oui',
    },
  },
  methods: {
    handleClick(e) {
      if (this.disabled) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    handleToggle() {
      if (!this.disabled) {
        this.$emit('change', !this.value);
      }
    },
  },
};
</script>

<style lang="scss">
.base-toggle {
  align-items: center;
  display: flex;
  font-size: 14px;

  &[disabled] {
    cursor: not-allowed;
    opacity: 0.4;

    .base-toggle__slider {
      cursor: not-allowed;
    }
  }

  // Its size whatever the label beside it: a long one wraps instead of squeezing it.
  &__switch {
    flex: none;
    position: relative;
    display: inline-block;
    width: 42px;
    height: 26px;
  }

  &__slider {
    background-color: transparent;
    bottom: 0;
    border: 2px solid $color-neutral;
    border-radius: $border-radius-large;
    cursor: pointer;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    transition: border 200ms ease;

    &:before {
      background-color: $color-neutral;
      border-radius: $border-radius-round;
      content: '';
      height: 16px;
      left: 3px;
      position: absolute;
      // Center vertically with equal auto margins between top:0/bottom:0 — the browser splits the
      // leftover space symmetrically, which rounds more stably under fractional zoom than a
      // percentage offset + translate. (Residual ~1px drift at some zooms is the bordered pill
      // itself rasterizing its top/bottom edges differently — inherent, not fixable via the knob.)
      top: 0;
      bottom: 0;
      margin-top: auto;
      margin-bottom: auto;
      transition: left 200ms ease;
      width: 16px;
    }
  }

  // The gap on the block, so a label on two lines keeps both beside the switch.
  &__label {
    min-width: 0;
    margin-left: 8px;
  }

  &__label-off {
    color: $color-normal;
  }

  &__label-on {
    color: $color-normal;
    font-weight: bold;
  }

  &__input {
    height: 0;
    opacity: 0;
    width: 0;

    &:checked {
      + .base-toggle__slider {
        border: 2px solid $color-green;

        &::before {
          background-color: $color-green;
          left: 19px;
        }
      }
    }

    &:focus {
      + .base-toggle__slider {
        box-shadow: $shadow-focus-outline;
      }
    }
  }

  &--is-small {
    font-size: 12px;

    .base-toggle__switch {
      height: 18px;
      width: 28px;
    }

    .base-toggle__slider::before {
      height: 12px;
      left: 2px;
      width: 12px;
    }
    .base-toggle__input:checked + .base-toggle__slider::before {
      left: 10px;
    }
  }
}
</style>
